import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
  const { t, fmt } = useApp();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);

  const loadSummary = () => {
    api.get("/reports/summary", { params: { start, end } }).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadSummary();
    api.get("/reports/monthly").then((r) => setMonthly(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-8">
      <PageHeader title={t("reports")} />
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">{t("periodSummary")}</h2>
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div className="space-y-1.5">
            <Label>{t("from")}</Label>
            <Input data-testid="report-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("to")}</Label>
            <Input data-testid="report-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11" />
          </div>
          <Button data-testid="report-generate-button" onClick={loadSummary} className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
            {t("generate")}
          </Button>
        </div>
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("income")}</div>
              <div data-testid="report-income" className="font-heading text-xl font-bold text-emerald-600 mt-1">{fmt(summary.income)}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("expense")}</div>
              <div data-testid="report-expense" className="font-heading text-xl font-bold text-red-500 mt-1">{fmt(summary.expense)}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("net")}</div>
              <div data-testid="report-net" className="font-heading text-xl font-bold text-slate-900 mt-1">{fmt(summary.net)}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("ordersCreated")}</div>
              <div data-testid="report-orders" className="font-heading text-xl font-bold text-sky-600 mt-1">{summary.orders}</div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">{t("monthlySummary")}</h2>
        <div className="h-72" data-testid="monthly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly}>
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar dataKey="income" name={t("income")} fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name={t("expense")} fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
